/** Admin routes (IA §2 Manage › Admin). Every view's state — tabs, filters, selection — is in the URL. */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';
import { ADMIN_PERMISSIONS } from './labels';

const oneOf = <T extends string>(v: unknown, values: readonly T[]): T | undefined => (values.includes(v as T) ? (v as T) : undefined);
const flag = (v: unknown): '1' | undefined => (v === '1' || v === 1 || v === true ? '1' : undefined);

export interface TabSearch {
  tab?: string;
}
const tabSearch = (s: Record<string, unknown>): TabSearch => ({ tab: str(s.tab) });

export const adminHubRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  staticData: { permission: ADMIN_PERMISSIONS },
  component: lazyRouteComponent(() => import('./hub/AdminHubPage')),
});

export const companyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/company',
  staticData: { permission: ['core.company.read', 'core.company.manage'] },
  validateSearch: tabSearch,
  component: lazyRouteComponent(() => import('./company/CompanyPage')),
});

export interface CompaniesSearch {
  q?: string;
  status?: 'active' | 'inactive' | 'all';
  selected?: string;
}

export const companiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/companies',
  staticData: { permission: 'platform.company.read' },
  validateSearch: (s: Record<string, unknown>): CompaniesSearch => ({
    q: str(s.q),
    status: oneOf(s.status, ['active', 'inactive', 'all'] as const),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./companies/CompaniesPage')),
});

export const companyDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/companies/$companyId',
  staticData: { permission: 'platform.company.read' },
  component: lazyRouteComponent(() => import('./companies/CompanyDetailPage')),
});

export interface ProjectsSearch {
  q?: string;
  status?: 'archived' | 'all';
  sort?: string;
  selected?: string;
}

export const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/projects',
  staticData: { permission: 'core.project.read' },
  validateSearch: (s: Record<string, unknown>): ProjectsSearch => ({
    q: str(s.q),
    status: oneOf(s.status, ['archived', 'all'] as const),
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./projects/ProjectsPage')),
});

/** The project page: its tab, plus the Settings tab's filters and selection. */
export interface ProjectSearch extends TabSearch {
  q?: string;
  category?: string;
  module?: string;
  changed?: '1';
  selected?: string;
}

export const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/projects/$projectId',
  staticData: { permission: 'core.project.read' },
  validateSearch: (s: Record<string, unknown>): ProjectSearch => ({
    tab: str(s.tab),
    q: str(s.q),
    category: str(s.category),
    module: str(s.module),
    changed: flag(s.changed),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./projects/ProjectPage')),
});

export interface UsersSearch {
  q?: string;
  status?: 'deactivated' | 'all';
  role?: string;
  origin?: 'own' | 'external';
  invite?: 'pending' | 'never_signed_in' | 'accepted';
  sort?: string;
  selected?: string;
  invite_open?: '1';
}

export const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/users',
  staticData: { permission: 'core.user.read' },
  validateSearch: (s: Record<string, unknown>): UsersSearch => ({
    q: str(s.q),
    status: oneOf(s.status, ['deactivated', 'all'] as const),
    role: str(s.role),
    origin: oneOf(s.origin, ['own', 'external'] as const),
    invite: oneOf(s.invite, ['pending', 'never_signed_in', 'accepted'] as const),
    sort: str(s.sort),
    selected: str(s.selected),
    invite_open: flag(s.invite_open),
  }),
  component: lazyRouteComponent(() => import('./users/UsersPage')),
});

export const userRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/users/$userId',
  staticData: { permission: 'core.user.read' },
  validateSearch: tabSearch,
  component: lazyRouteComponent(() => import('./users/UserPage')),
});

export interface RolesSearch {
  tab?: 'roles' | 'personas';
  q?: string;
  selected?: string;
}

export const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/roles',
  staticData: { permission: 'core.user.read' },
  validateSearch: (s: Record<string, unknown>): RolesSearch => ({
    tab: oneOf(s.tab, ['roles', 'personas'] as const),
    q: str(s.q),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./roles/RolesPage')),
});

export interface RoleSearch {
  module?: string;
  q?: string;
  only?: 'granted' | 'sensitive' | 'changed';
}

export const roleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/roles/$roleId',
  staticData: { permission: 'core.user.read' },
  validateSearch: (s: Record<string, unknown>): RoleSearch => ({
    module: str(s.module),
    q: str(s.q),
    only: oneOf(s.only, ['granted', 'sensitive', 'changed'] as const),
  }),
  component: lazyRouteComponent(() => import('./roles/RolePage')),
});

export interface GrantsSearch {
  role?: string;
  project?: string;
  origin?: 'own' | 'external';
  expiry?: 'expiring' | 'expired';
  sort?: string;
  grant?: '1';
}

export const grantsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/grants',
  staticData: { permission: 'core.user.read' },
  validateSearch: (s: Record<string, unknown>): GrantsSearch => ({
    role: str(s.role),
    project: str(s.project),
    origin: oneOf(s.origin, ['own', 'external'] as const),
    expiry: oneOf(s.expiry, ['expiring', 'expired'] as const),
    sort: str(s.sort),
    grant: flag(s.grant),
  }),
  component: lazyRouteComponent(() => import('./grants/GrantsPage')),
});

export interface ApiKeysSearch {
  q?: string;
  revoked?: '1';
  sort?: string;
  create?: '1';
}

export const apiKeysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/api-keys',
  staticData: { permission: 'core.apikey.manage' },
  validateSearch: (s: Record<string, unknown>): ApiKeysSearch => ({
    q: str(s.q),
    revoked: flag(s.revoked),
    sort: str(s.sort),
    create: flag(s.create),
  }),
  component: lazyRouteComponent(() => import('./apikeys/ApiKeysPage')),
});

export interface SettingsSearch {
  project?: string;
  q?: string;
  category?: string;
  module?: string;
  changed?: '1';
  selected?: string;
}

export const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/settings',
  staticData: { permission: 'core.setting.read' },
  validateSearch: (s: Record<string, unknown>): SettingsSearch => ({
    project: str(s.project),
    q: str(s.q),
    category: str(s.category),
    module: str(s.module),
    changed: flag(s.changed),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./settings/SettingsPage')),
});

export interface MatrixSearch {
  project?: string;
  status?: 'all' | 'inactive' | 'retired';
  differs?: '1';
  sort?: string;
}

export const settingMatrixRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/settings/$settingKey',
  staticData: { permission: 'core.setting.read' },
  validateSearch: (s: Record<string, unknown>): MatrixSearch => ({
    project: str(s.project),
    status: oneOf(s.status, ['all', 'inactive', 'retired'] as const),
    differs: flag(s.differs),
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./settings/SettingMatrixPage')),
});

export interface WaterfallSearch {
  q?: string;
  changed?: '1';
}

export const vehicleSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/settings/vehicles/$vehicleId',
  staticData: { permission: 'core.setting.read' },
  validateSearch: (s: Record<string, unknown>): WaterfallSearch => ({ q: str(s.q), changed: flag(s.changed) }),
  component: lazyRouteComponent(() => import('./settings/VehicleSettingsPage')),
});

export interface NewsSearch {
  status?: 'draft' | 'scheduled' | 'published' | 'archived';
  edit?: string;
}

export const newsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/news',
  staticData: { permission: 'admin.news.manage' },
  validateSearch: (s: Record<string, unknown>): NewsSearch => ({
    status: oneOf(s.status, ['draft', 'scheduled', 'published', 'archived'] as const),
    edit: str(s.edit),
  }),
  component: lazyRouteComponent(() => import('./news/NewsPage')),
});

export interface AccessLogSearch {
  actor?: string;
  resource?: string;
  subject?: string;
  sort?: string;
}

export const accessLogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/access-log',
  staticData: { permission: 'admin.accesslog.read' },
  validateSearch: (s: Record<string, unknown>): AccessLogSearch => ({
    actor: str(s.actor),
    resource: str(s.resource),
    subject: str(s.subject),
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./accesslog/AccessLogPage')),
});

export interface AiSearch {
  days?: '7' | '30' | '90' | '365';
}

export const aiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/ai',
  staticData: { permission: 'core.ai.admin' },
  validateSearch: (s: Record<string, unknown>): AiSearch => ({ days: oneOf(str(s.days), ['7', '30', '90', '365'] as const) }),
  component: lazyRouteComponent(() => import('./ai/AiPage')),
});

export const systemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/system',
  staticData: { permission: 'core.system.read' },
  component: lazyRouteComponent(() => import('./system/SystemPage')),
});

export const adminRoutes = [
  adminHubRoute,
  companyRoute,
  companiesRoute,
  companyDetailRoute,
  projectsRoute,
  projectRoute,
  usersRoute,
  userRoute,
  rolesRoute,
  roleRoute,
  grantsRoute,
  apiKeysRoute,
  settingsRoute,
  vehicleSettingsRoute,
  settingMatrixRoute,
  newsRoute,
  accessLogRoute,
  aiRoute,
  systemRoute,
] as const;
